import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Stack, Tab, TabProps, Tabs, TabsProps } from '@mui/material';
import BallotIcon from '@mui/icons-material/Ballot';
import FlagIcon from '@mui/icons-material/Flag';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import { UserMenuButton } from '../../UserMenuButton';
import { FindToolbox } from '../../FindToolbox';
import { ObjectToolbox } from '../../ObjectToolbox';
import { SiteList } from '../../SiteList';
import { useSettings } from '../../context/SettingsContext/useSettings';
import { FormattedMessage, useIntl } from 'react-intl';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const StyledTabs = styled((props: TabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ style: { display: 'none' } }} />
))({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: 40,
    width: '100%',
    backgroundColor: '#635ee7',
  },
});

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    minHeight: '48px',
    paddingInline: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.primary,
    '& > .MuiTab-iconWrapper': {
      marginRight: theme.spacing(0.5),
    },
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      background: theme.palette.action.selected,
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(100, 95, 228, 0.32)',
    },
  })
);

const DrawerContent = ({ handleLogoutClick }) => {
  const intl = useIntl();
  const {
    store: { mainDrawerIndex },
    setMainDrawerIndex,
  } = useSettings();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setMainDrawerIndex(newValue);
  };

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <StyledTabs
          value={mainDrawerIndex}
          onChange={handleChange}
          aria-label={intl.formatMessage({
            id: 'map.drawer.tabs',
            defaultMessage: 'Map panel tabs',
          })}
        >
          <StyledTab
            iconPosition="start"
            icon={<BallotIcon />}
            label={
              <FormattedMessage id="map.tab.finds" defaultMessage="Finds" />
            }
            aria-label={intl.formatMessage({
              id: 'map.tab.finds',
              defaultMessage: 'Finds',
            })}
          />
          <StyledTab
            iconPosition="start"
            icon={<FlagIcon />}
            label={
              <FormattedMessage id="map.tab.sites" defaultMessage="Sites" />
            }
            aria-label={intl.formatMessage({
              id: 'map.tab.sites',
              defaultMessage: 'Sites',
            })}
          />
          <StyledTab
            iconPosition="start"
            icon={<BubbleChartIcon />}
            label={
              <FormattedMessage id="map.tab.objects" defaultMessage="Objects" />
            }
            aria-label={intl.formatMessage({
              id: 'map.tab.objects',
              defaultMessage: 'Objects',
            })}
          />
        </StyledTabs>
        <Box mx={2}>
          <UserMenuButton onLogoutClick={handleLogoutClick} />
        </Box>
      </Stack>
      <Box px={3} py={1}>
        <TabPanel value={mainDrawerIndex} index={0}>
          <FindToolbox />
        </TabPanel>
        <TabPanel value={mainDrawerIndex} index={1}>
          <SiteList />
        </TabPanel>
        <TabPanel value={mainDrawerIndex} index={2}>
          <ObjectToolbox />
        </TabPanel>
      </Box>
    </Stack>
  );
};

export { DrawerContent };
